import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { i18n } from '@wms/shared';
import { api } from '../lib/api.js';
import { useSettings } from '../lib/settings-context.js';
import { PaidUntilChip } from '../components/PaidUntilChip.js';

const { t } = i18n;

interface CustomerRow {
  id: string;
  code: string;
  name: string;
  status: 'ACTIVE' | 'ARCHIVED';
  billingMode: 'PER_PALLET_DAY' | 'FIXED_MONTHLY';
  paidUntil: string | null;
  lastPaymentAt: string | null;
  _count: { pallets: number };
}

export function CustomersPage() {
  const settings = useSettings();
  const [rows, setRows] = useState<CustomerRow[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [includeArchived, setIncludeArchived] = useState(false);

  useEffect(() => {
    setRows(null);
    const qs = includeArchived ? '?includeArchived=true' : '';
    api<CustomerRow[]>(`/customers${qs}`)
      .then(setRows)
      .catch((err: unknown) => setError(String(err)));
  }, [includeArchived]);

  if (error) {
    return <div className="text-sm text-red-600">{error}</div>;
  }
  if (!rows) {
    return <div className="text-sm text-muted-foreground">{t('common.loading')}</div>;
  }

  return (
    <section className="space-y-4">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <h2 className="text-xl font-semibold">{t('customer.list.title')}</h2>
        <div className="flex items-center gap-3">
          <label className="flex items-center gap-1.5 text-sm">
            <input
              type="checkbox"
              checked={includeArchived}
              onChange={(e) => setIncludeArchived(e.target.checked)}
            />
            {t('common.show_archived')}
          </label>
          <Link
            to="/customers/new"
            className="rounded bg-primary text-primary-foreground px-3 py-1.5 text-sm"
          >
            {t('customer.list.create')}
          </Link>
        </div>
      </div>

      {rows.length === 0 ? (
        <div className="rounded border border-dashed border-border p-6 text-center text-sm text-muted-foreground">
          {t('customer.list.empty')}
        </div>
      ) : (
        <>
          <div className="hidden md:block overflow-x-auto rounded-md border border-border">
            <table className="w-full text-sm">
              <thead className="bg-muted text-left">
                <tr>
                  <th className="px-3 py-2 font-medium">{t('customer.col.code')}</th>
                  <th className="px-3 py-2 font-medium">{t('customer.col.name')}</th>
                  <th className="px-3 py-2 font-medium">{t('customer.col.billing_mode')}</th>
                  <th className="px-3 py-2 font-medium">{t('customer.col.active_pallets')}</th>
                  <th className="px-3 py-2 font-medium">{t('customer.col.paid_until')}</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => (
                  <tr
                    key={row.id}
                    className={`border-t border-border ${row.status === 'ARCHIVED' ? 'text-muted-foreground' : ''}`}
                  >
                    <td className="px-3 py-2 font-mono text-xs">{row.code}</td>
                    <td className="px-3 py-2">
                      <Link to={`/customers/${row.id}`} className="hover:underline">
                        {row.name}
                      </Link>
                      {row.status === 'ARCHIVED' ? (
                        <span className="ml-2 text-[10px] uppercase tracking-wide rounded bg-muted px-1 py-0.5">
                          {t('common.archived')}
                        </span>
                      ) : null}
                    </td>
                    <td className="px-3 py-2 text-xs">
                      {t(`customer.billing.${row.billingMode}`)}
                    </td>
                    <td className="px-3 py-2 tabular-nums">{row._count.pallets}</td>
                    <td className="px-3 py-2">
                      <PaidUntilChip
                        paidUntil={row.paidUntil}
                        thresholdDays={settings.paidUntilWarningDays}
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <ul className="md:hidden space-y-2">
            {rows.map((row) => (
              <li key={row.id} className="rounded-md border border-border p-3 space-y-2">
                <div className="flex items-start justify-between gap-2">
                  <Link to={`/customers/${row.id}`} className="font-medium hover:underline">
                    {row.name}
                  </Link>
                  <PaidUntilChip
                    paidUntil={row.paidUntil}
                    thresholdDays={settings.paidUntilWarningDays}
                  />
                </div>
                <dl className="grid grid-cols-2 gap-x-3 gap-y-0.5 text-xs">
                  <dt className="text-muted-foreground">{t('customer.col.code')}</dt>
                  <dd className="text-right font-mono">{row.code}</dd>
                  <dt className="text-muted-foreground">{t('customer.col.billing_mode')}</dt>
                  <dd className="text-right">{t(`customer.billing.${row.billingMode}`)}</dd>
                  <dt className="text-muted-foreground">{t('customer.col.active_pallets')}</dt>
                  <dd className="text-right tabular-nums">{row._count.pallets}</dd>
                </dl>
              </li>
            ))}
          </ul>
        </>
      )}
    </section>
  );
}
